// src/components/common/AppModal.tsx
import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import "./AppModal.scss";

interface AppModalProps {
  isOpen: boolean;
  onClose: () => void;
  width?: number | string;
  maxHeight?: number | string;
  /**
   * Floor height for the modal. Use to keep the dialog a stable size when
   * inner content (e.g., tabs) varies — prevents the centered modal from
   * jumping around the screen between tab switches.
   */
  minHeight?: number | string;
  children: React.ReactNode;
  showClose?: boolean;
  /** When true, clicking the overlay backdrop will NOT close the modal */
  persistent?: boolean;
  /** Optional header content rendered in a styled bar at the top of the modal. */
  header?: React.ReactNode;
  /** Apply the accent background color to the header (e.g., wildfire coral, VAR red). */
  headerAccent?: boolean;
}

/**
 * #968 — Escape dismissal lives here, once, instead of in each dialog.
 *
 * It is a module-level stack rather than a listener per instance, because dialogs
 * STACK: AppConfirm opens over other AppModals, and a listener per instance would
 * dismiss the dialog underneath at the same time. Only the topmost open dialog may
 * answer the key.
 *
 * The single listener sits in the CAPTURE phase on `window` — the first position in
 * the whole dispatch — and stops the event there. Observing the key without consuming
 * it is #888: a host modal that is still listening for Escape in order to close
 * itself runs too, and `WeatherStationCreateModal` raises a second confirmation over
 * the first. Dismissal calls `onClose`, which is the same path the close button and
 * the backdrop already take, so a dialog's existing guard — an unsaved-changes
 * prompt, a refusal while a delete is in flight — runs untouched.
 */
interface EscapeEntry {
  kind: "dialog" | "claim";
  onEscape: () => void;
  /** For a claim: the dialog it sits inside. */
  owner: EscapeEntry | null;
}

const escapeStack: EscapeEntry[] = [];
let escapeListener: ((event: KeyboardEvent) => void) | null = null;

const lastWhere = (predicate: (entry: EscapeEntry) => boolean): EscapeEntry | undefined => {
  for (let i = escapeStack.length - 1; i >= 0; i -= 1) {
    if (predicate(escapeStack[i])) return escapeStack[i];
  }
  return undefined;
};

/**
 * Resolution is by OWNERSHIP, not by push order.
 *
 * Push order cannot decide this: a claim inside a dialog's children registers its
 * effect BEFORE the dialog's own — React runs child effects first — so "the last
 * entry wins" hands Escape to the dialog and the claim never fires. That is the
 * regression this replaced.
 *
 * So: the topmost open dialog is found first, then the most recent claim belonging to
 * THAT dialog. A claim held by a dialog underneath is ignored, which is what makes a
 * confirm opening over a dialog mid-rename still close the confirm.
 */
const resolveEscapeTarget = (): EscapeEntry | undefined => {
  const topDialog = lastWhere((entry) => entry.kind === "dialog");
  if (!topDialog) return lastWhere((entry) => entry.kind === "claim");
  return lastWhere((entry) => entry.kind === "claim" && entry.owner === topDialog) ?? topDialog;
};

const handleEscape = (event: KeyboardEvent) => {
  if (event.key !== "Escape") return;
  const target = resolveEscapeTarget();
  if (!target) return;
  event.preventDefault();
  event.stopPropagation();
  target.onEscape();
};

const pushEscapeEntry = (entry: EscapeEntry) => {
  escapeStack.push(entry);
  if (!escapeListener) {
    escapeListener = handleEscape;
    window.addEventListener("keydown", escapeListener, true);
  }
};

const popEscapeEntry = (entry: EscapeEntry) => {
  const index = escapeStack.lastIndexOf(entry);
  if (index !== -1) escapeStack.splice(index, 1);
  if (escapeStack.length === 0 && escapeListener) {
    window.removeEventListener("keydown", escapeListener, true);
    escapeListener = null;
  }
};

/** Identifies the dialog a claim sits inside. */
const DialogEntryContext = React.createContext<EscapeEntry | null>(null);

/**
 * Lets a control INSIDE a dialog take Escape for itself while it is active.
 *
 * Needed because the listener above is in the capture phase, which is earlier than
 * anything React can see: React attaches its handlers at the root container, so an
 * in-dialog `onKeyDown` — even one calling `e.stopPropagation()` — never runs, and
 * Escape dismisses the whole dialog instead of cancelling the inline action. That is
 * exactly what happened to `LoadToolboxModal`'s rename field when Escape dismissal
 * moved in here.
 *
 * Bubble phase is NOT the alternative. In the #888 case the host modal registers its
 * own window listener BEFORE the confirm's AppModal mounts, so in bubble order the
 * host runs first and re-asks the question it was just answered. Capture is required
 * to beat it.
 *
 * So the claim is explicit and uses the same stack: pushed after the dialog it sits
 * in, and the topmost entry wins. That orders by intent rather than by whose effect
 * happened to register first.
 */
export function useEscapeClaim(active: boolean, onEscape: () => void): void {
  const owner = React.useContext(DialogEntryContext);
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!active) return undefined;
    const entry: EscapeEntry = {
      kind: "claim",
      owner,
      onEscape: () => onEscapeRef.current(),
    };
    pushEscapeEntry(entry);
    return () => popEscapeEntry(entry);
  }, [active, owner]);
}

/**
 * Component form of {@link useEscapeClaim}, for the common case where the claim is
 * driven by state held OUTSIDE the dialog's children — a `renamingId` on the dialog
 * component itself, say. The hook needs to run inside the dialog to learn which dialog
 * owns it, and a hook cannot be called conditionally or inside a `.map()`, so this
 * renders as a child instead.
 */
export const EscapeClaim: React.FC<{ active: boolean; onEscape: () => void }> = ({
  active,
  onEscape,
}) => {
  useEscapeClaim(active, onEscape);
  return null;
};

const AppModal: React.FC<AppModalProps> = ({
  isOpen,
  onClose,
  width = 420,
  maxHeight,
  minHeight,
  children,
  showClose = true,
  persistent = false,
  header,
  headerAccent = false,
}) => {
  // Read through a ref so registering does not depend on the identity of an inline
  // arrow prop — nearly every call site passes one, and re-registering on each render
  // would reorder the stack underneath an open dialog.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // One stable entry per mounted dialog. It doubles as this dialog's identity, which
  // is what a claim inside it is matched against, so it must not change per render.
  const entryRef = useRef<EscapeEntry | null>(null);
  if (!entryRef.current) {
    entryRef.current = { kind: "dialog", owner: null, onEscape: () => onCloseRef.current() };
  }
  const entry = entryRef.current;

  useEffect(() => {
    if (!isOpen) return undefined;
    pushEscapeEntry(entry);
    return () => popEscapeEntry(entry);
  }, [isOpen, entry]);

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (!persistent) {
      onClose();
    }
  };

  const modalClass = ['app-modal', header ? 'app-modal--has-header' : '']
    .filter(Boolean)
    .join(' ');
  const headerClass = ['app-modal__header', headerAccent ? 'app-modal__header--accent' : '']
    .filter(Boolean)
    .join(' ');

  return ReactDOM.createPortal(
    <DialogEntryContext.Provider value={entry}>
    <div className="app-modal__overlay" onClick={handleOverlayClick}>
      <div
        className={modalClass}
        style={{
          width,
          ...(maxHeight && { maxHeight }),
          ...(minHeight && { minHeight }),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {showClose && (
          <button
            className="app-modal__close-icon"
            onClick={onClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        )}

        {header && <div className={headerClass}>{header}</div>}

        {/* Scrollable area so the close button stays fixed */}
        <div className="app-modal__body">{children}</div>
      </div>
    </div>
    </DialogEntryContext.Provider>,
    document.body
  );
};

export default AppModal;
